import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/authContext';
import { AppLayout } from '../components/AppLayout';
import { StatusBadge } from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Clock, Wrench, CheckCircle2, XCircle, AlertCircle, Download, TrendingUp, Users } from 'lucide-react';
import api from '../lib/api';
import { PRACTICE_STATUS, USER_ROLES } from '../lib/constants';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';

export default function DashboardPage() {
  const { user } = useAuth();
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');

  useEffect(() => {
    loadPractices();
  }, [selectedStatus]);

  const loadPractices = async () => {
    try {
      setLoading(true);
      const params = selectedStatus !== 'all' ? { status: selectedStatus } : {};
      const response = await api.get('/api/practices', { params });
      setPractices(response.data.practices);
    } catch (error) {
      console.error('Failed to load practices:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    [PRACTICE_STATUS.IN_ATTESA_PRESA_IN_CARICO]: practices.filter(
      (p) => p.status === PRACTICE_STATUS.IN_ATTESA_PRESA_IN_CARICO
    ).length,
    [PRACTICE_STATUS.IN_LAVORAZIONE]: practices.filter(
      (p) => p.status === PRACTICE_STATUS.IN_LAVORAZIONE
    ).length,
    [PRACTICE_STATUS.DA_INTEGRARE]: practices.filter(
      (p) => p.status === PRACTICE_STATUS.DA_INTEGRARE
    ).length,
    [PRACTICE_STATUS.COMPLETATA_IN_COMITATO]: practices.filter(
      (p) => p.status === PRACTICE_STATUS.COMPLETATA_IN_COMITATO
    ).length,
    [PRACTICE_STATUS.RIFIUTATA]: practices.filter((p) => p.status === PRACTICE_STATUS.RIFIUTATA)
      .length,
    total: practices.length,
    totalPower: practices
      .filter((p) => p.status === PRACTICE_STATUS.COMPLETATA_IN_COMITATO)
      .reduce((sum, p) => sum + (p.input_data?.potenza_stimata || 0), 0)
      .toFixed(2),
    successRate: practices.length > 0 
      ? ((practices.filter(p => p.status === PRACTICE_STATUS.COMPLETATA_IN_COMITATO).length / practices.length) * 100).toFixed(1)
      : 0,
    avgPower: practices.filter(p => p.status === PRACTICE_STATUS.COMPLETATA_IN_COMITATO).length > 0
      ? (practices
          .filter((p) => p.status === PRACTICE_STATUS.COMPLETATA_IN_COMITATO)
          .reduce((sum, p) => sum + (p.input_data?.potenza_stimata || 0), 0) / 
          practices.filter(p => p.status === PRACTICE_STATUS.COMPLETATA_IN_COMITATO).length).toFixed(2)
      : 0,
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/api/practices/export', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `atacama_solar_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export completato');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Errore durante l export');
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground">Benvenuto, {user?.full_name}</p>
          </div>
          <div className="flex gap-2">
            {(user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.UFFICIO_TECNICO) && (
              <Button variant="outline" onClick={handleExport}>
                <Download className="mr-2 h-4 w-4" />
                Esporta CSV
              </Button>
            )}
            {(user?.role === USER_ROLES.SEGNALATORE || user?.role === USER_ROLES.UFFICIO_TECNICO || user?.role === USER_ROLES.ADMIN) && (
              <Link to="/practices/new">
                <Button data-testid="dashboard-create-practice-button">
                  <Plus className="mr-2 h-4 w-4" />
                  Nuova Pratica
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <Card className="card-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                In Attesa
              </CardTitle>
              <Clock className="h-4 w-4 text-slate-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums">
                {stats[PRACTICE_STATUS.IN_ATTESA_PRESA_IN_CARICO]}
              </div>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                In Lavorazione
              </CardTitle>
              <Wrench className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums">
                {stats[PRACTICE_STATUS.IN_LAVORAZIONE]}
              </div>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Da Integrare
              </CardTitle>
              <AlertCircle className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums">
                {stats[PRACTICE_STATUS.DA_INTEGRARE]}
              </div>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Completate
              </CardTitle>
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums">
                {stats[PRACTICE_STATUS.COMPLETATA_IN_COMITATO]}
              </div>
            </CardContent>
          </Card>

          <Card className="card-shadow bg-primary text-primary-foreground">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium opacity-90">
                Potenza Totale
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums">
                {stats.totalPower} kW
              </div>
              <p className="text-xs opacity-75 mt-1">Impianti completati</p>
            </CardContent>
          </Card>
        </div>

        {/* Additional KPIs */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="card-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Tasso Successo
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-emerald-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums">
                {stats.successRate}%
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats[PRACTICE_STATUS.COMPLETATA_IN_COMITATO]} completate su {stats.total} totali
              </p>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Potenza Media
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-blue-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums">
                {stats.avgPower} kW
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Per impianto completato
              </p>
            </CardContent>
          </Card>

          <Card className="card-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Totale Pratiche
                </CardTitle>
                <Users className="h-4 w-4 text-slate-500" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold tabular-nums">
                {stats.total}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {stats[PRACTICE_STATUS.IN_ATTESA_PRESA_IN_CARICO] + stats[PRACTICE_STATUS.IN_LAVORAZIONE] + stats[PRACTICE_STATUS.DA_INTEGRARE]} attive
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Practices Table */}
        <Card>
          <CardHeader>
            <CardTitle>Pratiche</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={selectedStatus} onValueChange={setSelectedStatus}>
              <TabsList>
                <TabsTrigger value="all">Tutte</TabsTrigger>
                <TabsTrigger value={PRACTICE_STATUS.IN_ATTESA_PRESA_IN_CARICO}>
                  In Attesa
                </TabsTrigger>
                <TabsTrigger value={PRACTICE_STATUS.IN_LAVORAZIONE}>In Lavorazione</TabsTrigger>
                <TabsTrigger value={PRACTICE_STATUS.COMPLETATA_IN_COMITATO}>
                  Completate
                </TabsTrigger>
              </TabsList>

              <div className="mt-4">
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
                ) : practices.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Nessuna pratica trovata
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Città</TableHead>
                        <TableHead>Potenza (kW)</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead>Creata</TableHead>
                        <TableHead>Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {practices.map((practice) => (
                        <TableRow key={practice.id}>
                          <TableCell className="font-medium">
                            {practice.input_data.nome_referente}
                          </TableCell>
                          <TableCell>{practice.input_data.citta}</TableCell>
                          <TableCell className="font-mono">
                            {practice.input_data.potenza_stimata}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={practice.status} />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDistanceToNow(new Date(practice.created_at), {
                              addSuffix: true,
                              locale: it,
                            })}
                          </TableCell>
                          <TableCell>
                            <Link to={`/practices/${practice.id}`}>
                              <Button variant="ghost" size="sm">
                                Dettagli
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
